import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { cn } from '@repo/ui/lib/utils';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { authClient } from '@/providers/session';

export const ZSendConfirmationEmailFormSchema = z.object({
  email: z.email().min(1),
});

export type TSendConfirmationEmailFormSchema = z.infer<typeof ZSendConfirmationEmailFormSchema>;

export type SendConfirmationEmailFormProps = {
  className?: string;
};

export const SendConfirmationEmailForm = ({ className }: SendConfirmationEmailFormProps) => {
  useLocale();

  const form = useForm<TSendConfirmationEmailFormSchema>({
    values: {
      email: '',
    },
    resolver: zodResolver(ZSendConfirmationEmailFormSchema, { error: validationError }),
  });

  const isSubmitting = form.formState.isSubmitting;

  const onFormSubmit = async ({ email }: TSendConfirmationEmailFormSchema) => {
    try {
      await authClient.emailPassword.resendVerifyEmail({ email });

      toast.success(t('auth.confirmationEmailSent'), {
        get description() {
          return t('auth.aConfirmationEmailHasBeenSentAndItShould');
        },
        duration: 5000,
      });

      form.reset();
    } catch (err) {
      toast.error(t('auth.anErrorOccurredWhileSendingYourConfirmationEmail'), {
        get description() {
          return t('auth.pleaseTryAgainAndMakeSureYouEnterThe');
        },
      });
    }
  };

  return (
    <Form {...form}>
      <form
        className={cn('mt-6 flex w-full flex-col gap-y-4', className)}
        onSubmit={form.handleSubmit(onFormSubmit)}
      >
        <fieldset className="flex w-full flex-col gap-y-4" disabled={isSubmitting}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.emailAddress')}</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormMessage />

          <Button disabled={isSubmitting} loading={isSubmitting} size="lg" type="submit">
            {t('auth.sendConfirmationEmail')}{' '}
          </Button>
        </fieldset>
      </form>
    </Form>
  );
};
